// The corner window's HUD. The scene stays clear: a vellum ledger beside or under it carries the hunt, the lead and
// the trainer's XP and the latest catches (hover for the rolls, click to open the creature in the game). Its ▾ drops
// the rest down over the scene: this session's rates and every catch, sortable. Battery saver puts the ledger in the
// scene's place and stops drawing it. Banners mark the moments worth a look, and the edge glows gold while an Aura is
// on the hunt. The tools (layout, zoom, battery saver, chime, back to the game) show while the pointer is over the
// window; with the scene out of sight only the last three, which have room kept for them.
import { SPECIES } from '../../shared/species.ts';
import { GROUND, AREA, REGIONS } from '../../shared/grounds.ts';
import { creatureXpToNext, trainerXpToNext } from '../../shared/xp.ts';
import { displayName } from '../../shared/nametags.ts';
import { qualityPercent } from '../../shared/rolls.ts';
import { gradeOf } from '../../shared/grade.ts';
import type { CreatureDTO } from '../../shared/protocol.ts';
import { S, serverNow } from '../state.ts';
import { esc, full, num, estimate, RISK_TEXT } from '../fmt.ts';
import { creatureIcon, spriteUrl, uiIcon } from '../assets.ts';
import { rollPills } from '../rollpills.ts';
import { placeBeside } from '../tip.ts';
import { gradeBadge, crest } from '../badges.ts';
import { leadCreature, huntState, sessionTotals, lastKillXp } from '../hud.ts';
import { statusText } from '../status.ts';
import { goldenNow, goldenName } from '../golden.ts';
import { MomentQueue, perHour, shapeOf, sceneRect, sideWidth, sortCatches, rollsOf, CAP_SORTS, isCapSort, type CapSort, type Caught, type Moment, type Shape } from './model.ts';

export interface HudHooks {
  /** A layout button: resize the window to that layout's size. */
  shape(s: Shape): void;
  zoom(dir: 1 | -1): void;
  /** Battery saver on or off. */
  saver(): void;
  sound(): void;
  /** Bring the game's tab forward, at an address when given (#/creature/12). */
  game(hash?: string): void;
  /** A click on the Golden Wild (client/golden.ts). */
  claim(): void;
}

const GRADE_CLASS: Record<string, string> = { C: 'c', B: 'b', A: 'a', S: 's', 'S+': 'sp' };
const SVG = (d: string) => `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true">${d}</svg>`;
// Fallbacks for the rig's ui-corner-* icons (art/rig3d/icons), which have a drawing made for 16 px.
const TOOL_SVG = {
  strip: SVG('<rect x="1.5" y="9" width="13" height="4.5" rx=".5"/><path d="M1.5 3h13" opacity=".45"/>'),
  card: SVG('<rect x="7" y="7.5" width="7.5" height="6" rx=".5"/><path d="M1.5 2.5h13v11" opacity=".45"/>'),
  column: SVG('<rect x="10" y="2" width="4.5" height="12" rx=".5"/><path d="M1.5 2v12" opacity=".45"/>'),
  minus: SVG('<path d="M4 8h8"/>'),
  plus: SVG('<path d="M4 8h8M8 4v8"/>'),
  saver: SVG('<rect x="1.5" y="4.5" width="11" height="7" rx="1"/><path d="M14.5 7v2M4.5 7v2M7 7v2"/>'),
  bell: SVG('<path d="M4 11.5V7.5a4 4 0 0 1 8 0v4l1.2 1.2H2.8zM6.6 13.8a1.5 1.5 0 0 0 2.8 0"/>'),
  game: SVG('<path d="M9 2.5h4.5V7M13.5 2.5 7.5 8.5M12 9.5v4H2.5V4h4"/>'),
};
// Read when the window opens: the art list arrives after the game's first load. Battery saver borrows the hunt
// view's own icon until it has a corner drawing.
const toolIcon = (k: keyof typeof TOOL_SVG) => uiIcon(`ui-corner-${k}`, k === 'saver' ? uiIcon('ui-saver', TOOL_SVG.saver, 'cv-ic', 16) : TOOL_SVG[k], 'cv-ic', 16);

const timeOf = (at: number) => new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const clock = (ms: number) => { const m = Math.floor(ms / 60_000); return m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`; };
const pct = (f: number) => `${Math.floor(Math.max(0, Math.min(1, f)) * 100)}%`;
const SORT_KEY = 'mi-corner-sort';

type Part = 'hunt' | 'pic' | 'lead' | 'tr' | 'chips' | 'more' | 'grid' | 'cap' | 'list' | 'sort' | 'banner' | 'glow' | 'veil' | 'tip' | 'tools' | 'lg' | 'golden';

export class CornerHud {
  shape: Shape = 'card';
  /** Battery saver: no scene, the ledger fills the window. Set by the window. */
  saver = false;
  /** The ▾ drop-down: rates and every catch over the scene (card and strip; the column always shows them). */
  open = false;
  soundOn = false;
  private sort: CapSort = (() => { try { const v = localStorage.getItem(SORT_KEY); return isCapSort(v) ? v : 'recent'; } catch { return 'recent'; } })();
  /** Chips that fit in the ledger's row of latest catches. */
  private slots = 6;
  private el: Record<Part, HTMLElement>;
  private sig = new Map<string, string>();
  private queue = new MomentQueue();
  private shown: Moment | null = null;
  private lastCatch = -1;
  private tipFor = 0;

  private root: HTMLElement;
  private win: Window;
  private hooks: HudHooks;

  constructor(root: HTMLElement, win: Window, hooks: HudHooks) {
    this.root = root; this.win = win; this.hooks = hooks;
    root.className = 'cv';
    root.insertAdjacentHTML('beforeend', `
      <div class="cv-veil" hidden></div>
      <div class="cv-banner" hidden></div>
      <section class="cv-lg" aria-label="This session">
        <div class="lg-hunt clickable" role="link"></div>
        <div class="lg-pic clickable" role="link"></div>
        <div class="lg-lead clickable" role="link"></div>
        <div class="lg-tr"></div>
        <div class="lg-chips"></div>
        <button class="lg-more" aria-expanded="false" title="Rates and every catch"></button>
        <div class="lg-grid"></div>
        <div class="lg-cap">
          <div class="lg-caph"><b>Catches</b><select class="lg-sort" aria-label="Sort catches">${CAP_SORTS.map(([k, n]) => `<option value="${k}">${n}</option>`).join('')}</select></div>
          <div class="lg-list"></div>
        </div>
      </section>
      <div class="cv-glow"></div>
      <button class="golden cv-golden" type="button" hidden></button>
      <nav class="cv-tools" aria-label="Window">
        <button data-scene data-act="shape" data-v="strip" title="Strip: a low band along the screen" aria-label="Strip layout">${toolIcon('strip')}</button>
        <button data-scene data-act="shape" data-v="card" title="Card: a box in a corner" aria-label="Card layout">${toolIcon('card')}</button>
        <button data-scene data-act="shape" data-v="column" title="Column: a band down the side" aria-label="Column layout">${toolIcon('column')}</button>
        <span data-scene class="gap"></span>
        <button data-scene data-act="zoom" data-v="-1" title="Zoom out" aria-label="Zoom out">${toolIcon('minus')}</button>
        <button data-scene data-act="zoom" data-v="1" title="Zoom in" aria-label="Zoom in">${toolIcon('plus')}</button>
        <span data-scene class="gap"></span>
        <button data-act="saver" title="Battery saver" aria-label="Battery saver" aria-pressed="false">${toolIcon('saver')}</button>
        <button data-act="sound" title="Chime for Auras" aria-label="Chime for Auras" aria-pressed="false">${toolIcon('bell')}</button>
        <button data-act="game" title="Back to the game" aria-label="Back to the game">${toolIcon('game')}</button>
      </nav>
      <div class="cv-tip" hidden></div>`);
    const q = (s: string) => root.querySelector(s) as HTMLElement;
    this.el = {
      hunt: q('.lg-hunt'), pic: q('.lg-pic'), lead: q('.lg-lead'), tr: q('.lg-tr'), chips: q('.lg-chips'), more: q('.lg-more'),
      grid: q('.lg-grid'), cap: q('.lg-cap'), list: q('.lg-list'), sort: q('.lg-sort'), lg: q('.cv-lg'),
      banner: q('.cv-banner'), glow: q('.cv-glow'), veil: q('.cv-veil'), tip: q('.cv-tip'), tools: q('.cv-tools'), golden: q('.cv-golden'),
    };
    (this.el.sort as HTMLSelectElement).value = this.sort;
    this.bind();
  }

  private bind() {
    const { el, hooks } = this;
    el.tools.addEventListener('click', (e) => {
      const b = (e.target as Element).closest('button') as HTMLButtonElement | null;
      if (!b) return;
      if (b.dataset.act === 'shape') hooks.shape(b.dataset.v as Shape);
      else if (b.dataset.act === 'zoom') hooks.zoom(Number(b.dataset.v) > 0 ? 1 : -1);
      else if (b.dataset.act === 'saver') hooks.saver();
      else if (b.dataset.act === 'sound') hooks.sound();
      else if (b.dataset.act === 'game') hooks.game();
    });
    const openLead = () => { const c = leadCreature(); hooks.game(c ? `#/creature/${c.id}` : '#/creatures/team'); };
    el.hunt.addEventListener('click', () => { const g = GROUND[S.trainer?.ground ?? '']; hooks.game(g ? `#/map/${g.region}/${g.area}` : '#/map'); });
    el.pic.addEventListener('click', openLead);
    el.lead.addEventListener('click', openLead);
    el.more.addEventListener('click', () => this.setOpen(!this.open));
    el.golden.addEventListener('click', () => hooks.claim());
    el.sort.addEventListener('change', () => {
      const v = (el.sort as HTMLSelectElement).value;
      if (!isCapSort(v)) return;
      this.sort = v;
      try { localStorage.setItem(SORT_KEY, v); } catch { /* storage off */ }
      el.list.scrollTop = 0;
      this.refresh();
    });
    this.win.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.open) this.setOpen(false); });
    for (const host of [el.chips, el.list]) {
      host.addEventListener('click', (e) => {
        const t = (e.target as Element).closest('[data-cap]') as HTMLElement | null;
        const id = Number(t?.dataset.cap);
        if (id && S.creatures.has(id)) hooks.game(`#/creature/${id}`);
      });
      host.addEventListener('pointerover', (e) => {
        const t = (e.target as Element).closest('[data-cap]') as HTMLElement | null;
        if (t) this.showTip(t); else this.hideTip();
      });
      host.addEventListener('pointerleave', () => this.hideTip());
    }
  }

  setOpen(on: boolean) {
    this.open = on;
    this.el.more.setAttribute('aria-expanded', String(on));
    this.hideTip();
    this.layout();
  }

  /** Whether the scene is on screen (not in battery saver, not under the drop-down), so worth drawing. */
  get sceneShown() { return !this.saver && !(this.open && this.shape !== 'column'); }

  /** Chooses the layout for the window's size and places the scene; the ledger takes the rest. */
  layout() {
    const w = this.win.innerWidth, h = this.win.innerHeight;
    const shape = (this.shape = shapeOf(w, h));
    const r = this.root;
    r.dataset.shape = shape;
    r.toggleAttribute('data-saver', this.saver);
    // The column shows everything already; the drop-down is for the card and the strip.
    r.toggleAttribute('data-open', this.open && shape !== 'column' && !this.saver);
    r.toggleAttribute('data-tight', shape === 'card' ? w < 360 : shape === 'strip' ? h < 120 : w < 260);
    const sc = sceneRect(shape, w, h, this.saver);
    for (const [k, v] of Object.entries({ x: sc?.x ?? 0, y: sc?.y ?? 0, w: sc?.w ?? 0, h: sc?.h ?? 0 })) r.style.setProperty(`--s${k}`, `${v}px`);
    // The strip's panel keeps its width when the ▾ spreads the ledger along the band.
    r.style.setProperty('--side-mid', `${Math.max(120, sideWidth(w) - 112)}px`);
    this.refresh();
    // Chips that fit the row the layout gives them.
    const slots = Math.max(0, Math.min(16, Math.floor((this.el.chips.clientWidth + 3) / 33)));
    if (slots !== this.slots) { this.slots = slots; this.sig.delete('chips'); this.refresh(); }
  }

  /** Writes html into a part only when its content changed, so walkers keep walking and hovers hold. */
  private put(key: string, host: HTMLElement, html: string) {
    if (this.sig.get(key) === html) return false;
    this.sig.set(key, html);
    host.innerHTML = html;
    return true;
  }

  /** Brings every part up to date with the game's state. Cheap: run it a couple of times a second. */
  refresh() {
    const t = S.trainer, doc = this.root.ownerDocument;
    // Themes tint their chrome by the hunt's region.
    const region = document.documentElement.dataset.region;
    if (region && doc.documentElement.dataset.region !== region) doc.documentElement.dataset.region = region;
    const veil = this.veil();
    if (!t) return;
    const g = GROUND[t.ground];
    const hs = huntState();
    const lead = leadCreature();
    if (g) {
      const est = lead ? estimate(lead, g) : null;
      const reg = REGIONS.find((x) => x.id === g.region);
      this.el.hunt.title = `${AREA[g.area].name}${reg ? ` · ${reg.name}` : ''}${est ? ` · ${est.secondsPerKill.toFixed(1)} s per kill · ${RISK_TEXT[est.risk]}` : ''} · ${hs.text}`;
      // In battery saver there is no scene to veil, so its news (resting, offline) rides on this line.
      const note = this.saver && veil ? veil : AREA[g.area].name;
      this.put('hunt', this.el.hunt, `<i class="seal ${hs.cls}"></i><b>${esc(SPECIES[g.species].name)}</b><span class="lv">Lv ${full(g.level)}</span><small>${esc(note)}</small>`);
    }
    this.lead(lead);
    const need = trainerXpToNext(t.level), frac = t.xp / need;
    this.bar('tr', this.el.tr, `<b>Trainer</b><span>Lv ${full(t.level)}</span><em>${pct(frac)}</em>`, frac, `Trainer XP ${full(t.xp)} / ${full(need)}`);
    const s = sessionTotals();
    this.chips(s.caught);
    this.put('more', this.el.more, `<b>${full(s.catches)}</b><i class="car" aria-hidden="true"></i>`);
    this.el.more.setAttribute('aria-label', `${full(s.catches)} caught this session: rates and every catch`);
    // An Aura standing on the hunt keeps the edge glowing until it is caught or gone.
    const aura = !!S.field?.wilds.some((w) => w.aura && !(w.downUntil && w.tried && !w.throwing));
    if (!this.el.glow.classList.contains('flash')) this.el.glow.classList.toggle('on', aura);
    if (this.shape === 'column' || this.saver || this.open) { this.grid(lead, s); this.list(s.caught); }
    this.golden();
  }

  /** The Golden Wild (client/golden.ts): where it stands on the scene, or in the window's corner with the scene out of sight. */
  golden() {
    const b = this.el.golden, g = goldenNow();
    b.hidden = !g;
    if (!g) { delete b.dataset.id; return; }
    if (b.dataset.id !== String(g.id)) {
      b.dataset.id = String(g.id);
      const name = goldenName(g.species);
      b.title = `${name} · click for coins and XP`;
      b.setAttribute('aria-label', `${name}: click for coins and XP`);
      const ran = Math.max(0, g.ms - (g.until - Date.now()));
      b.innerHTML = `<span class="golden-pic">${creatureIcon(g.species, false, '')}</span><span class="golden-time"><i style="animation-duration:${g.ms}ms;animation-delay:-${ran}ms"></i></span>`;
    }
    const float = !this.sceneShown;
    b.classList.toggle('float', float);
    b.style.left = float ? '' : `calc(var(--sx) + var(--sw) * ${g.x})`;
    b.style.top = float ? '' : `calc(var(--sy) + var(--sh) * ${g.y})`;
  }

  /** A label line over a thin bar; the bar moves every tick, the label only when its text does. */
  private bar(key: string, host: HTMLElement, label: string, frac: number, title: string) {
    if (this.put(key, host, `<div class="lb">${label}</div><span class="bar2 xp"><i></i></span>`)) host.title = title;
    else if (host.title !== title) host.title = title;
    (host.querySelector('.bar2 > i') as HTMLElement).style.width = `${(Math.max(0, Math.min(1, frac)) * 100).toFixed(2)}%`;
  }

  private lead(c: CreatureDTO | undefined) {
    const { pic, lead } = this.el;
    pic.hidden = lead.hidden = !c;
    if (!c) return;
    const hp = S.field?.teamHp[c.id];
    const hpFrac = hp ? hp.hp / hp.maxHp : 1;
    this.put('pic', pic, `${creatureIcon(c.species, c.rolls.aura)}<span class="hp"><i></i></span>`);
    const hpBar = pic.querySelector('.hp') as HTMLElement;
    hpBar.className = `hp${hpFrac <= 0.2 ? ' low' : hpFrac <= 0.5 ? ' mid' : ''}`;
    (hpBar.firstElementChild as HTMLElement).style.width = `${(Math.max(0, hpFrac) * 100).toFixed(1)}%`;
    pic.classList.toggle('out', hpFrac <= 0);
    pic.title = `${displayName(c)} · HP ${hp ? `${full(hp.hp)} / ${full(hp.maxHp)}` : 'full'}`;
    const need = creatureXpToNext(c.level), per = lastKillXp();
    this.bar('lead', lead, `<b${c.rolls.aura ? ' class="aura"' : ''}>${esc(displayName(c))}</b><span>Lv ${full(c.level)}</span><em>${pct(c.xp / need)}</em>`, c.xp / need,
      `${displayName(c)} · XP ${full(c.xp)} / ${full(need)}${per ? ` · +${full(per)} per kill` : ''}`);
  }

  private chip(x: Caught, fresh: boolean) {
    const c = x.c, grade = gradeOf(rollsOf(c).rating);
    return `<span class="cv-chip ${GRADE_CLASS[grade]}${c.rolls.aura ? ' aura' : ''}${x.sold ? ' sold' : ''}${fresh ? ' new' : ''}" data-cap="${c.id}"><img src="${spriteUrl(c.species, c.rolls.aura)}" alt="${esc(SPECIES[c.species]?.name ?? '')}"><span class="g">${grade}</span></span>`;
  }

  /** The latest catches, newest first, as many as fit. */
  private chips(list: Caught[]) {
    const last = list.length ? list[list.length - 1].c.id : 0;
    const fresh = this.lastCatch >= 0 && last !== this.lastCatch ? last : 0;
    const shown = list.slice(-this.slots).reverse();
    const key = `${this.slots}|${shown.map((x) => `${x.c.id}${x.sold ? 's' : ''}`).join(',')}`;
    if (this.sig.get('chips') !== key) {
      this.sig.set('chips', key);
      this.el.chips.innerHTML = shown.map((x) => this.chip(x, x.c.id === fresh)).join('');
    }
    this.lastCatch = last;
  }

  /** Returns what the scene's veil says (empty when there is nothing to say). */
  private veil(): string {
    const v = this.el.veil;
    const f = S.field, now = serverNow();
    let head = '', sub = '', cls = '';
    if (!S.trainer) { head = 'Waiting for the game'; }
    else if (!S.online) { head = 'Offline'; sub = `Hunting is paused · ${statusText()}`; cls = 'bad'; }
    else if (S.trainer.stopped) { head = 'In Kinhall'; sub = 'Your team fainted'; cls = 'bad'; }
    else if (f && f.restingUntil > now) { head = 'Resting'; sub = `Back in ${Math.ceil((f.restingUntil - now) / 1000)} s`; }
    else if (f && !f.lead) { head = 'No one is hunting'; sub = 'Add a creature to your team'; }
    v.hidden = !head || (this.saver && !!S.trainer);
    v.className = `cv-veil ${cls}`;
    this.put('veil', v, head ? `<b>${esc(head)}</b>${sub ? `<span>${esc(sub)}</span>` : ''}` : '');
    return head && sub ? `${head} · ${sub}` : head;
  }

  /** This session at a glance: rates once five minutes have passed, then the totals. */
  private grid(lead: CreatureDTO | undefined, s: ReturnType<typeof sessionTotals>) {
    const ms = Date.now() - s.start;
    const rate = (v: number, what: string) => {
      const r = perHour(v, ms);
      return r === null ? { v: '—', t: 'Rates show after 5 minutes' } : { v: num(r), t: `${full(r)} ${what} per hour · ${full(v)} this session` };
    };
    const cells: [string, { v: string; t: string }][] = [
      ['Trainer XP/h', rate(s.xp - s.xpLost, 'trainer XP')],
      ['Lead XP/h', lead ? rate(s.leadXp[lead.id] ?? 0, `XP for ${displayName(lead)}`) : { v: '—', t: 'No lead' }],
      ['Coins/h', rate(s.coins, 'coins')],
      ['Defeated/h', rate(s.kills, 'defeated')],
      ['Caught/h', rate(s.catches, 'caught')],
      ['Session', { v: clock(ms), t: `Since ${timeOf(s.start)}` }],
      ['Caught', { v: full(s.catches), t: `${full(s.kills)} defeated this session` }],
      ['Auras seen', { v: full(s.aurasSeen), t: 'Auras that appeared on your hunt this session' }],
      ['Auras caught', { v: full(s.aurasCaught), t: 'Auras caught this session' }],
    ];
    this.put('grid', this.el.grid, cells.map(([label, x]) => `<div title="${esc(x.t)}"><b>${x.v}</b><small>${label}</small></div>`).join(''));
  }

  /** Every catch this session in the chosen order; the right edge shows what the order is by. */
  private list(caught: Caught[]) {
    const rows = sortCatches(caught, this.sort).slice(0, 120);
    const meta = (x: Caught): string => {
      const r = rollsOf(x.c);
      switch (this.sort) {
        case 'grade': return `${r.rating}`;
        case 'talent': return `${r.talent}%`;
        case 'quality': { const q = qualityPercent(x.c.rolls); return `${q > 0 ? '+' : q < 0 ? '−' : ''}${Math.abs(q)}%`; }
        case 'level': return `Lv ${full(x.c.level)}`;
        default: return timeOf(x.at);
      }
    };
    const html = rows.map((x) => {
      const c = x.c;
      return `<div class="row${x.sold ? ' sold' : ''}${c.rolls.aura ? ' aura' : ''}" data-cap="${c.id}"><img src="${spriteUrl(c.species, c.rolls.aura)}" alt="">
        <span class="who"><b>${esc(displayName(c))}</b>${gradeBadge(rollsOf(c).rating, 'sm')}${crest(c.rolls.bloodline)}</span><span class="m">${meta(x)}</span></div>`;
    }).join('');
    this.put('list', this.el.list, html || '<p class="empty">No catches yet this session.</p>');
  }

  private showTip(target: HTMLElement) {
    const id = Number(target.dataset.cap);
    const x = sessionTotals().caught.find((e) => e.c.id === id);
    if (!x) return this.hideTip();
    const tip = this.el.tip, c = x.c;
    if (this.tipFor !== id) {
      this.tipFor = id;
      const kept = S.creatures.has(id), team = S.trainer?.team.includes(id), fav = S.trainer?.collection?.includes(id);
      const where = x.sold ? `Released · +${full(x.sold)} coins` : !kept ? 'No longer yours' : team ? 'On your team' : fav ? 'In Favorites' : 'In storage';
      tip.innerHTML = `<div class="t1"><b>${esc(displayName(c))}</b><span>Lv ${full(c.level)}</span><time>${timeOf(x.at)}</time></div>${rollPills(c, { rollsOnly: true })}
        <div class="note">${where}</div>`;
    }
    tip.hidden = false;
    // Beside a list row, above or below a chip; always inside the window.
    placeBeside(tip, target, target.closest('.lg-list') ? { prefer: 'right', margin: 6 } : { prefer: 'above', margin: 6 });
  }
  private hideTip() { this.el.tip.hidden = true; this.tipFor = 0; }

  setSound(on: boolean) {
    this.soundOn = on;
    this.el.tools.querySelector('[data-act="sound"]')!.setAttribute('aria-pressed', String(on));
  }

  setSaver(on: boolean) {
    this.saver = on;
    this.el.tools.querySelector('[data-act="saver"]')!.setAttribute('aria-pressed', String(on));
    if (on) this.open = false;
    this.el.more.setAttribute('aria-expanded', String(this.open));
  }

  /** New moments from the hunt: queue their banners. An Aura caught also flashes the edge. */
  moments(list: Moment[]) {
    if (!list.length) return;
    this.queue.push(list);
    if (list.some((m) => m.chime === 'aura-caught')) {
      const glow = this.el.glow;
      glow.classList.remove('on', 'flash');
      void glow.offsetWidth;
      glow.classList.add('flash');
      this.win.setTimeout(() => glow.classList.remove('flash'), 1500);
    }
  }

  /** Per animation frame: swaps banners as their time runs out. */
  frame(now: number) {
    const m = this.queue.current(now);
    if (m === this.shown) return;
    this.shown = m;
    const b = this.el.banner;
    b.hidden = !m;
    if (!m) return;
    b.textContent = m.text;
    b.className = `cv-banner ${m.tone}`;
    void b.offsetWidth;
    b.classList.add('show');
  }

  clearMoments() { this.queue.clear(); }
}
